import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiAward, FiUsers, FiTrendingUp, FiHeart, FiTarget, FiClock, FiArrowRight, FiArrowLeft, FiMail, FiPhone, FiMapPin, FiSearch, FiX } = FiIcons;

const AboutPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  const [lightboxIndex, setLightboxIndex] = React.useState(0);
  const [currentGallery, setCurrentGallery] = React.useState('');

  const teamImages = [
    'https://i.ibb.co/d44RvrLL/2-G0-A6446-scaled.jpg',
    'https://i.ibb.co/yB5Xp4KC/2-G0-A6498-scaled.jpg',
    'https://i.ibb.co/0VWMjxsp/2-G0-A6597-1-scaled.jpg',
    'https://i.ibb.co/v65sfrZR/2-G0-A6466-1-scaled.jpg',
    'https://i.ibb.co/Y7xC62jg/Whats-App-Image-2023-10-10-at-01-20-46-600x899.jpg'
  ];

  const testimonialImages = [
    'https://i.ibb.co/xKV9x8Fz/Snehal-Toshniwal-Testimonial-2.png',
    'https://i.ibb.co/vWGjR6H/Dhiraj-Gandhi-Testimonial.png',
    'https://i.ibb.co/ks7F4Kxk/Chirag-Kaswa-Testimonial.png',
    'https://i.ibb.co/HTcPxj1S/Jidnya-Shetiya-Testimonial.png'
  ];

  const charityImages = [
    'https://i.ibb.co/0jRsj1YT/Whats-App-Image-2024-01-20-at-9-52-57-PM-1-scaled.jpg',
    'https://i.ibb.co/VnCkFMY/Whats-App-Image-2024-01-20-at-9-52-57-PM-3-scaled.jpg',
    'https://i.ibb.co/SwxpZ0zM/Whats-App-Image-2024-01-20-at-9-53-04-PM-scaled.jpg'
  ];

  const openLightbox = (index, gallery) => {
    setLightboxIndex(index);
    setCurrentGallery(gallery);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    const images = currentGallery === 'team' ? teamImages :
                   currentGallery === 'testimonial' ? testimonialImages : charityImages;
    setLightboxIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    const images = currentGallery === 'team' ? teamImages :
                   currentGallery === 'testimonial' ? testimonialImages : charityImages;
    setLightboxIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#F9FFF5] to-[#EAF8EC] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div className="inline-block">
                <span className="bg-[#2E7D32] text-white px-4 py-2 rounded-full text-sm font-medium">
                  The Sam's Nutri Hub Story
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0B1320] font-poppins leading-tight">
                How a personal health crisis became a mission to transform lives
              </h1>
              <blockquote className="text-xl md:text-2xl text-[#4A4A4A] italic font-merriweather leading-relaxed">
                "What if nutrition could be simple, effective, and actually enjoyable?"
              </blockquote>
              <div className="space-y-4 text-[#4A4A4A] leading-relaxed">
                <p>
                  That question cost me three years of my life. It was 2018. I was sitting in yet another doctor's office, clutching a folder full of conflicting diet plans, supplement recommendations, and exercise regimens that were supposed to be "working together" to improve my health. They weren't. Instead, I was bouncing between specialists, trying contradictory advice, and watching my health deteriorate because the pieces weren't fitting together into a cohesive approach.
                </p>
                <p>
                  That's when it hit me: The individual recommendations weren't the problem. The integration was. What I needed wasn't another diet plan—I needed a holistic approach that understood my unique body, lifestyle, and goals, working with me as a complete system rather than a collection of symptoms. That's when Sam's Nutri Hub was born.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/body-analysis"
                  className="bg-[#2E7D32] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#1B5E20] transition-all duration-200 transform hover:translate-y-[-1px] hover:shadow-lg text-center"
                >
                  Start Your Free Assessment
                </Link>
                <Link
                  to="/counselling"
                  className="border border-[#2E7D32] text-[#2E7D32] bg-white px-8 py-4 rounded-lg font-semibold hover:bg-[#2E7D32] hover:text-white transition-all duration-200 transform hover:translate-y-[-1px] hover:shadow-lg text-center"
                >
                  Get Nutrition Counselling
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img
                src="https://i.ibb.co/d0W0cD1k/IMG-6088-converted-1691465676-scaled.jpg"
                alt="Sam's Nutri Hub team."
                className="rounded-2xl shadow-2xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Three-Year Health Journey */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1320] font-poppins mb-12 text-center">
              The Three-Year Health Journey That Changed Everything
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="space-y-6 text-[#4A4A4A] leading-relaxed">
                <p>
                  Before I tell you what we built, let me explain why that question cost me three years.
                </p>
                <p>
                  That was the time I lost to:
                </p>
              </div>
              <div className="space-y-6 text-[#4A4A4A] leading-relaxed">
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <span className="text-[#2E7D32] font-bold">•</span>
                    <span>Trying fad diets that worked briefly, then backfired</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#2E7D32] font-bold">•</span>
                    <span>Following exercise programs that weren't sustainable with my lifestyle</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#2E7D32] font-bold">•</span>
                    <span>Taking supplements my body didn't actually need</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#2E7D32] font-bold">•</span>
                    <span>Ignoring the crucial mind-body connection in wellness</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#2E7D32] font-bold">•</span>
                    <span>And the opportunity cost of not living with full energy and vitality when I had the chance</span>
                  </li>
                </ul>
                <p>
                  But here's the interesting part—when I started talking to friends and family about my experience, I discovered I wasn't alone. In fact, most people were suffering from the same problem, just with different symptoms.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Most Nutrition Plans Fail */}
      <section className="bg-[#FFFDF8] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1320] font-poppins mb-12 text-center">
              Why Most Nutrition Plans Fail You
            </h2>
            <div className="max-w-4xl mx-auto space-y-8 text-[#4A4A4A] leading-relaxed">
              <p>
                The typical approach to nutrition and wellness goes something like this:
              </p>
              <p className="italic">
                "I need to lose weight. Let's try that diet."
                "Now I need more energy. Let's add those supplements."
                "My doctor says my cholesterol is high. I should cut out these foods."
              </p>
              <p>
                Before you know it, you're juggling 7 different approaches with 7 different rules, 7 different learning curves, and 7 different ways they can conflict with each other. And when they inevitably do conflict, you're left wondering which one failed and why.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Nutrition Experts Who Understand Real Life */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1320] font-poppins mb-12 text-center">
              Nutrition Experts Who Understand Real Life
            </h2>
            <div className="max-w-4xl mx-auto space-y-8 text-[#4A4A4A] leading-relaxed mb-16">
              <p>
                Sam's Nutri Hub isn't your typical nutrition company. Our team fundamentally comes from diverse backgrounds in nutrition science, fitness coaching, and behavioral psychology. We're trained to look at your health holistically, identify root causes, and build solutions that last.
              </p>
              <p>
                But we've chosen to apply these skills to real people living real lives—not laboratory conditions.
              </p>
              <p>
                This means when we look at your nutrition, exercise habits, or wellness journey, we're not thinking: "What's the quick fix to make you lose 10 pounds this month?" We're thinking: "What's the underlying structure that will make you healthy and energetic for decades?" It's the difference between a crash diet and rebuilding your relationship with food.
              </p>
            </div>
            <motion.div
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              className="grid grid-cols-2 md:grid-cols-5 gap-6"
            >
              {teamImages.map((image, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="overflow-hidden rounded-xl shadow-lg cursor-pointer group"
                  onClick={() => openLightbox(index, 'team')}
                >
                  <img
                    src={image}
                    alt="Sam's Nutri Hub team."
                    className="w-full h-48 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <SafeIcon icon={FiSearch} className="h-8 w-8 text-white" />
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Impact Metrics */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1320] font-poppins mb-12 text-center">
              Real-World Impact
            </h2>
            <motion.div
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
            >
              {[
                {
                  number: "5+",
                  label: "Years of Experience"
                },
                {
                  number: "1000+",
                  label: "Lives Transformed"
                },
                {
                  number: "20+",
                  label: "Customized Programs"
                }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-[#2E7D32]"
                >
                  <div className="text-5xl font-bold text-[#2E7D32] mb-4 font-poppins">
                    {stat.number}
                  </div>
                  <p className="text-[#4A4A4A]">
                    {stat.label}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Assess · Adapt · Achieve · Repeat */}
      <section className="bg-[#EAF8EC] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1320] font-poppins mb-12 text-center">
              Assess. Adapt. Achieve. Repeat.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              {[
                {
                  number: "1",
                  title: "Assess",
                  desc: "We gather data about your body, lifestyle, preferences, and goals."
                },
                {
                  number: "2",
                  title: "Adapt",
                  desc: "We build nutrition and wellness plans that align with your unique needs."
                },
                {
                  number: "3",
                  title: "Achieve",
                  desc: "We measure results and collect insights on what's working."
                },
                {
                  number: "4",
                  title: "Repeat",
                  desc: "We refine and improve based on your real-world progress."
                }
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="bg-[#FAD74C] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="text-2xl font-bold text-[#0B1320]">{step.number}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-[#0B1320] mb-4 font-poppins">{step.title}</h3>
                  <p className="text-[#4A4A4A]">{step.desc}</p>
                </div>
              ))}
            </div>
            <div className="text-center">
              <Link
                to="/body-analysis"
                className="inline-block border-2 border-[#2E7D32] text-[#2E7D32] bg-white px-8 py-4 rounded-lg font-semibold hover:bg-[#2E7D32] hover:text-white transition-colors duration-200"
              >
                See How the Body Analysis Works
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What We Actually Do */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1320] font-poppins mb-12 text-center">
              What We Actually Do
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-12">
              <div>
                <h3 className="text-xl font-semibold text-[#0B1320] mb-6 font-poppins">Personalized Nutrition Plans</h3>
                <ul className="space-y-3 text-[#4A4A4A]">
                  <li className="flex items-start space-x-3">
                    <span className="text-[#2E7D32] font-bold">•</span>
                    <span>Custom meal plans that fit your lifestyle and preferences</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#2E7D32] font-bold">•</span>
                    <span>Nutritional guidance for specific health conditions</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#2E7D32] font-bold">•</span>
                    <span>Supplement recommendations that actually make sense for your body</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#2E7D32] font-bold">•</span>
                    <span>Sustainable eating strategies that don't feel like "dieting"</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#2E7D32] font-bold">•</span>
                    <span>Food relationship coaching to heal emotional eating patterns</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-[#0B1320] mb-6 font-poppins">Fitness Integration</h3>
                <ul className="space-y-3 text-[#4A4A4A]">
                  <li className="flex items-start space-x-3">
                    <span className="text-[#2E7D32] font-bold">•</span>
                    <span>Exercise recommendations that complement your nutrition</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#2E7D32] font-bold">•</span>
                    <span>Movement plans that fit into your busy schedule</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#2E7D32] font-bold">•</span>
                    <span>Strength and mobility coaching for all fitness levels</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#2E7D32] font-bold">•</span>
                    <span>Recovery strategies to prevent burnout and injury</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#2E7D32] font-bold">•</span>
                    <span>Progress tracking that celebrates all victories, big and small</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-[#0B1320] mb-6 font-poppins">Holistic Wellness Support</h3>
                <ul className="space-y-3 text-[#4A4A4A]">
                  <li className="flex items-start space-x-3">
                    <span className="text-[#2E7D32] font-bold">•</span>
                    <span>Stress management techniques that actually work</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#2E7D32] font-bold">•</span>
                    <span>Sleep optimization strategies for better recovery</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#2E7D32] font-bold">•</span>
                    <span>Mindfulness practices that improve your relationship with food</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#2E7D32] font-bold">•</span>
                    <span>Community support to keep you motivated and accountable</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#2E7D32] font-bold">•</span>
                    <span>Ongoing education to empower your health decisions</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/body-analysis"
                className="bg-[#2E7D32] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#1B5E20] transition-all duration-200 transform hover:translate-y-[-1px] hover:shadow-lg text-center"
              >
                Start Your Free Assessment
              </Link>
              <Link
                to="/plans"
                className="border border-[#2E7D32] text-[#2E7D32] bg-white px-8 py-4 rounded-lg font-semibold hover:bg-[#2E7D32] hover:text-white transition-all duration-200 transform hover:translate-y-[-1px] hover:shadow-lg text-center"
              >
                Explore Plans
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why This Matters To You */}
      <section className="bg-[#FFFDF8] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1320] font-poppins mb-12 text-center">
              Why This Matters To You
            </h2>
            <div className="max-w-4xl mx-auto space-y-8 text-[#4A4A4A] leading-relaxed">
              <p>
                Let me be clear: We're not for everyone.
              </p>
              <p>
                If you're looking for the "diet hack of the week" or want a magic pill for overnight results, we're probably not a good fit.
              </p>
              <p>
                But if you're building health for the long haul—wellness that creates real vitality and serves you for decades—then our approach might be exactly what you need.
              </p>
              <p>
                Because here's the truth: The people who win at health aren't the ones with the trendiest diets or the most expensive supplements.
              </p>
              <p>
                They're the ones with systems that work reliably, day in and day out, without constant struggle.
              </p>
              <p>
                They're the ones who can focus on living their lives instead of fighting with their bodies.
              </p>
              <p>
                They're the ones who have a partner in their wellness journey—someone who understands both the science and the human side of the equation.
              </p>
              <p>
                Whether that means creating a custom meal plan that works with your busy schedule, developing an exercise strategy that energizes rather than exhausts you, or implementing stress management techniques that fit your lifestyle—we're here to be that partner.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Decision Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1320] font-poppins mb-12 text-center">
              The Decision Is Yours
            </h2>
            <motion.div
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12"
            >
              {[
                "Keep doing what you're doing (and getting the same results)",
                "Try to figure out a better approach yourself (which takes time and expertise)",
                "Let us be your guide—your partner in wellness—and build a health system that works for you"
              ].map((option, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="bg-[#F9FFF5] p-8 rounded-xl shadow-lg text-center"
                >
                  <div className="text-5xl font-bold text-[#2E7D32] mb-4 font-poppins">
                    {index + 1}
                  </div>
                  <p className="text-[#4A4A4A]">
                    {option}
                  </p>
                </motion.div>
              ))}
            </motion.div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/body-analysis"
                className="bg-[#2E7D32] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#1B5E20] transition-all duration-200 transform hover:translate-y-[-1px] hover:shadow-lg text-center"
              >
                Begin Your Transformation
              </Link>
              <Link
                to="/counselling"
                className="border border-[#2E7D32] text-[#2E7D32] bg-white px-8 py-4 rounded-lg font-semibold hover:bg-[#2E7D32] hover:text-white transition-all duration-200 transform hover:translate-y-[-1px] hover:shadow-lg text-center"
              >
                Speak to a Coach
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonial Snippets */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1320] font-poppins mb-12 text-center">
              What Our Clients Say
            </h2>
            <motion.div
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              className="grid grid-cols-2 md:grid-cols-4 gap-6"
            >
              {testimonialImages.map((image, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="overflow-hidden rounded-xl shadow-lg cursor-pointer group"
                  onClick={() => openLightbox(index, 'testimonial')}
                >
                  <img
                    src={image}
                    alt="Client transformation testimonial."
                    className="w-full h-48 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <SafeIcon icon={FiSearch} className="h-8 w-8 text-white" />
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Community Impact */}
      <section className="bg-[#EAF8EC] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1320] font-poppins mb-12 text-center">
              Giving Back — Free Events for Kids
            </h2>
            <motion.div
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
            >
              {charityImages.map((image, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="overflow-hidden rounded-xl shadow-lg cursor-pointer group"
                  onClick={() => openLightbox(index, 'charity')}
                >
                  <img
                    src={image}
                    alt="Sam's Nutri Hub free kids wellness event."
                    className="w-full h-64 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <SafeIcon icon={FiSearch} className="h-8 w-8 text-white" />
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#205C25] py-20 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-3xl md:text-4xl font-bold text-white font-poppins mb-6">
              Let's Talk About Your Health Journey
            </h3>
            <p className="text-xl text-gray-200 mb-8 max-w-3xl mx-auto">
              P.S. Remember those three years I mentioned? The wellness system we built to solve my own health crisis ended up helping over 1,000 clients transform their lives. Sometimes our biggest struggles lead to our greatest purpose. What could your breakthrough be?
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <Link
                to="/body-analysis"
                className="bg-white text-[#205C25] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-200 transform hover:translate-y-[-1px] hover:shadow-lg text-center"
              >
                Start Your Free Assessment
              </Link>
              <Link
                to="/counselling"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#205C25] transition-all duration-200 transform hover:translate-y-[-1px] hover:shadow-lg text-center"
              >
                Schedule a Consultation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white p-2 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Close lightbox"
          >
            <SafeIcon icon={FiX} className="h-8 w-8" />
          </button>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
            className="absolute left-4 text-white p-3 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Previous image"
          >
            <SafeIcon icon={FiArrowLeft} className="h-8 w-8" />
          </button>
          
          <div 
            className="max-w-4xl max-h-[90vh] cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={
                currentGallery === 'team' ? teamImages[lightboxIndex] :
                currentGallery === 'testimonial' ? testimonialImages[lightboxIndex] : charityImages[lightboxIndex]
              }
              alt="Full size view"
              className="max-h-[80vh] object-contain"
            />
          </div>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
            className="absolute right-4 text-white p-3 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Next image"
          >
            <SafeIcon icon={FiArrowRight} className="h-8 w-8" />
          </button>
        </div>
      )}
    </div>
  );
};

export default AboutPage;